import Typography from "@mui/material/Typography";
import { CustomerDatabaseStore } from "../../../../../store/main/CustomerDatabaseStore";
import { TourProgramApprovalStore } from "../../../../../store/main/TourProgramApprovalStore";
import { useEffect, useState } from "react";
import Status from "../../../../../common/enums/Status";
import Swal from "sweetalert2";
import { ReportFilters } from "../../../../../domain/models/report-filters";
import moment from "moment";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import ReportFiltersComponent from "../../ReportFiltersComponent";
import ReportFiltersLoading from "../../ReportFiltersLoading";
import ReportScreen from "../../../../../common/enums/ReportScreens";
import TourApprovalConfirmationModal from "../Modal/TourApprovalConfirmationModal";
import {
  TourApproval,
  TourPagination,
} from "../../../../../domain/models/tour-program-approval/fetch-tour-program-approval";
import {
  TourDeviationApprovals,
  TourDeviationPagination,
} from "../../../../../domain/models/tour-program-approval/fetch-tour-deviation-approval";

const commonReturnTableStyle = {
  border: "1px solid #e0e0e0",
  py: 0.1,
};

type FetchTourDeviationApprovalParams = {
  month: string;
  year: number;
  q: string;
  status?: string;
  role_id?: number | undefined;
  zone_ids?: number[];
  region_ids?: number[];
  area_ids?: number[];
  headquarter_ids?: number[];
  page: number;
};

const TourDeviationApproval = () => {
  const { userDetails, decodeToken, userRole } = CustomerDatabaseStore();
  const {
    fetchTourDeviationApproval,
    FetchTourDeviationApprovalState,
    FetchTourDeviationApproval,
    addTourDeviationApproval,
    AddTourDeviationApprovalState,
  } = TourProgramApprovalStore();

  const currentDate = new Date();
  const defaultMonth = currentDate.getMonth() + 1;
  const defaultYear = currentDate.getFullYear();
  const [month, setMonth] = useState<number>(defaultMonth);
  const [year, setYear] = useState<number>(defaultYear);
  const [loading, setLoading] = useState<boolean>(false);
  const [tourDeviationApprovalData, setTourDeviationApprovalData] = useState<
    TourDeviationApprovals[]
  >([]);
  const [pagination, setPagination] = useState<TourDeviationPagination | null>(
    null
  );
  const [page, setPage] = useState<number>(1);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTourId, setSelectedTourId] = useState<number | null>(null);
  const [forcedAction, setForcedAction] = useState<
    "approve" | "reject" | undefined
  >(undefined);
  const [isSubmitting, setIsSubmitting] = useState<null | "approve" | "reject">(
    null
  );

  const defaultFilters = {
    month: defaultMonth.toString().padStart(2, "0"),
    year: defaultYear,
    q: "",
    status: "pending",
    role_id: undefined,
    zone_ids: [],
    region_ids: [],
    area_ids: [],
    headquarter_ids: [],
    page: 1,
  };

  const [currentFilters, setCurrentFilters] =
    useState<FetchTourDeviationApprovalParams>(defaultFilters);

  const handleSelectRow = (id: number) => {
    const tour = tourDeviationApprovalData.find((t) => t.id === id);
    if (tour?.status === Status.ACTIVE || tour?.status === Status.IN_ACTIVE)
      return;

    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const isSelected = (id: number) => selectedRows.includes(id);
  const handleSingleApprove = (tourId: number) => {
    setSelectedTourId(tourId);
    setForcedAction("approve");
    setModalOpen(true);
  };

  const handleSingleReject = (tourId: number) => {
    setSelectedTourId(tourId);
    setForcedAction("reject");
    setModalOpen(true);
  };

  const handleBulkAction = () => {
    setSelectedTourId(null);
    setForcedAction(undefined);
    setModalOpen(true);
  };

const handleConfirmAction = async (
  status: Status.ACTIVE | Status.IN_ACTIVE
) => {
  setIsSubmitting(status === Status.ACTIVE ? "approve" : "reject");

  const payload = {
    month: month.toString().padStart(2, "0"),
    year: year,
    ids: selectedTourId !== null ? [selectedTourId] : selectedRows,
    status,
  };

  try {
    const response = await addTourDeviationApproval(payload);

    if (response?.success) {
      const totalIds = payload.ids.length;
      const skippedCount = response?.skipped_ids?.length || 0;
      const successCount = totalIds - skippedCount;

      const actionWord = status === Status.ACTIVE ? "approved" : "rejected";

      // 🟢 Build success message
      let successMessage = `${successCount} tour${
        successCount !== 1 ? "s" : ""
      } ${actionWord} successfully.`;

      if (skippedCount > 0) {
        successMessage += ` However, ${skippedCount} tour${
          skippedCount !== 1 ? "s" : ""
        } were skipped.`;
      }

      Swal.fire({
        icon: skippedCount > 0 ? "warning" : "success",
        title: skippedCount > 0 ? "Partial Success" : "Success",
        text: successMessage,
        timer: 3000,
        showConfirmButton: false,
      });

      setModalOpen(false);
      setSelectedRows([]);
      setSelectedTourId(null);

      // Remove only successfully processed ones
      setTourDeviationApprovalData((prev) =>
        prev.filter((t) => !payload.ids.includes(t.id))
      );

      fetchTourDeviationApprovalData({ ...currentFilters, page: 1 });
    } else {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text:
          response?.errors?.message ||
          response?.message ||
          "Something went wrong",
      });
    }
  } catch (err: any) {
    console.error("API error:", err);

    const errorMessage =
      err?.response?.data?.errors?.message ||
      err?.response?.data?.message ||
      err?.message ||
      "Something went wrong. Please try again.";

    Swal.fire({
      icon: "error",
      title: "Error",
      text: errorMessage,
    });
  } finally {
    setIsSubmitting(null);
  }
};


  useEffect(() => {
    decodeToken();
    fetchTourDeviationApprovalData({ ...currentFilters, page: 1 });
  }, []);

  const fetchTourDeviationApprovalData = async (params: {
    month: string;
    year: number;
    q?: string;
    status?: string;
    role_id?: number | null;
    zone_ids?: number[];
    region_ids?: number[];
    area_ids?: number[];
    headquarter_ids?: number[];
    page?: number;
  }) => {
    setLoading(true);
    try {
      const response = await fetchTourDeviationApproval(params);
      if (response.success) {
        setTourDeviationApprovalData(response.tours);
        setPagination(response.paginatedData);
      } else {
        console.error("API returned failure:", response);
      }
    } catch (err) {
      console.error("Failed to fetch tour approval data:", err);
    } finally {
      setLoading(false);
    }
  };

  const filterData = async (filter: ReportFilters) => {
    const month = parseInt(moment(filter.date?.toString()).format("MM"));
    const year = parseInt(moment(filter.date?.toString()).format("YYYY"));
    setMonth(month);
    setYear(year);
    const monthString = month.toString().padStart(2, "0");
    const params = {
      ...defaultFilters,
      month: monthString,
      year: year,
      q: filter.search?.toString() ?? "",
      status: filter.status?.toString() ?? "pending",
      role_id: filter.role ? Number(filter.role) : undefined,
      zone_ids: Array.isArray(filter.zone) ? filter.zone.map(Number) : [],
      region_ids: Array.isArray(filter.region) ? filter.region.map(Number) : [],
      area_ids: Array.isArray(filter.area) ? filter.area.map(Number) : [],
      headquarter_ids: Array.isArray(filter.headquarter)
        ? filter.headquarter.map(Number)
        : [],
      page: page,
    };
    setCurrentFilters(params);
    setPage(1);
    setSelectedRows([]);
    fetchTourDeviationApprovalData({ ...params, page: 1 });
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);

    fetchTourDeviationApprovalData({
      ...currentFilters,
      page: value,
    });
  };

  const clearData = async () => {
    setMonth(defaultMonth);
    setYear(defaultYear);
    setCurrentFilters(defaultFilters);
    setPage(1);
    setSelectedRows([]);
    fetchTourDeviationApprovalData({ ...defaultFilters, page: 1 });
  };

  return (
    <>
      <Box pl={2} pt={1}>
        <Box mt={2}>
          {Object.keys(userDetails).length > 0 ? (
            <ReportFiltersComponent
              type={ReportScreen.TOUR_PROGRAM_APPROVAL}
              successCallback={filterData}
              clearSuccessCallBack={clearData}
              userRole={userRole.role}
              userDetails={userDetails}
              gridColumns={24}
              territoryMultiSelect={false}
            />
          ) : (
            <ReportFiltersLoading />
          )}
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : tourDeviationApprovalData.length === 0 ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="80vh"
          textAlign="center"
        >
          <Typography variant="h3" color="text.secondary">
            Approval Data is not available for this month.
          </Typography>
        </Box>
      ) : (
        <>
          <Box display="flex" justifyContent="flex-end" mb={2} pr={5}>
            <Button
              variant="contained"
              disabled={selectedRows.length <= 1}
              onClick={handleBulkAction}
            >
              Bulk Action
            </Button>
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              mt: 3,
              ml: "auto",
              mr: "auto",
              width: "100%",
              maxWidth: "95%",
              overflowX: "auto",
              maxHeight: 500,
              overflowY: "auto",
            }}
          >
            <Table
              stickyHeader
              aria-label="input-tour-table"
              sx={{ minWidth: 650 }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...commonReturnTableStyle }}>
                    <Checkbox
                      checked={
                        selectedRows.length ===
                          tourDeviationApprovalData.filter(
                            (t) =>
                              t.status !== Status.ACTIVE &&
                              t.status !== Status.IN_ACTIVE
                          ).length &&
                        tourDeviationApprovalData.some(
                          (t) =>
                            t.status !== Status.ACTIVE &&
                            t.status !== Status.IN_ACTIVE
                        )
                      }
                      indeterminate={
                        selectedRows.length > 0 &&
                        selectedRows.length <
                          tourDeviationApprovalData.filter(
                            (t) =>
                              t.status !== Status.ACTIVE &&
                              t.status !== Status.IN_ACTIVE
                          ).length
                      }
                      onChange={(e) =>
                        setSelectedRows(
                          e.target.checked
                            ? tourDeviationApprovalData
                                .filter(
                                  (t) =>
                                    t.status !== Status.ACTIVE &&
                                    t.status !== Status.IN_ACTIVE
                                )
                                .map((t) => t.id)
                            : []
                        )
                      }
                    />
                  </TableCell>

                  {[
                    "Name",
                    "Designation",
                    "Tour Type",
                    "Tour Brick",
                    "HQ",
                    "Date",
                    "Visited Brick Name",
                    "Visited Work Type",
                    "Deviation Type",
                    "Tour Comment",
                    "Status",
                  ].map((header) => (
                    <TableCell
                      key={header}
                      align="center"
                      sx={{
                        fontWeight: "bold",
                        border: "1px solid #ccc",
                        backgroundColor: "#0D71B7",
                        color: "#fff",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        py: 1,

                        minWidth: header === "Date" ? 80 : { xs: 80, sm: 100 },

                        display: {
                          xs:
                            header === "Type" || header === "HQ"
                              ? "none"
                              : "table-cell",
                          sm: "table-cell",
                        },
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {tourDeviationApprovalData.map((tour) => (
                  <TableRow key={tour.id} selected={isSelected(tour.id)}>
                    <TableCell sx={commonReturnTableStyle}>
                      <Checkbox
                        checked={isSelected(tour.id)}
                        onChange={() => handleSelectRow(tour.id)}
                      />
                    </TableCell>
                    <TableCell sx={commonReturnTableStyle}>
                      {tour.user ?? "—"}
                    </TableCell>
                    <TableCell sx={commonReturnTableStyle}>
                      {tour.role ?? "—"}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...commonReturnTableStyle,
                        display: { xs: "none", sm: "table-cell" },
                      }}
                    >
                      {tour.tour_types ?? "—"}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...commonReturnTableStyle,
                        display: { xs: "none", sm: "table-cell" },
                      }}
                    >
                      {tour.tour_bricks ?? "—"}
                    </TableCell>
                    <TableCell sx={commonReturnTableStyle}>
                      {tour.tour_headquarters &&
                      tour.tour_headquarters.trim() !== ""
                        ? "-"
                        : tour.tour_territories ?? "—"}
                    </TableCell>
                    <TableCell
                      sx={{
                        border: "1px solid #e0e0e0",
                        minWidth: 60,
                        textAlign: "left",
                      }}
                    >
                      {tour.date ?? "—"}
                    </TableCell>
                    <TableCell sx={commonReturnTableStyle}>
                      <Tooltip
                        title={
                          <Typography sx={{ color: "black" }}>
                            {tour.visited_bricks ?? "—"}
                          </Typography>
                        }
                        arrow
                        placement="top"
                        slotProps={{
                          tooltip: {
                            sx: {
                              bgcolor: "white",
                              color: "black",
                              boxShadow: 3,
                              border: "1px solid rgba(233, 20, 20, 0.6)",
                            },
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            color: "black",
                          }}
                        >
                          {tour.visited_bricks ?? "—"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={commonReturnTableStyle}>
                      {tour.visited_types ?? "—"}
                    </TableCell>
                    <TableCell sx={commonReturnTableStyle}>
                      {tour.deviation_type ?? "—"}
                    </TableCell>
                    <TableCell
                      sx={{ ...commonReturnTableStyle, maxWidth: 100 }}
                    >
                      <Tooltip
                        title={
                          <Typography sx={{ color: "black" }}>
                            {tour.reason ?? "—"}
                          </Typography>
                        }
                        arrow
                        placement="top"
                        slotProps={{
                          tooltip: {
                            sx: {
                              bgcolor: "white", // background white
                              color: "black", // text black
                              boxShadow: 3, // small shadow for clarity
                              border: "1px solid rgba(233, 20, 20, 0.6)",
                            },
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            color: "black", // make sure cell text is also black
                          }}
                        >
                          {tour.reason ?? "—"}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Actions */}
                    <TableCell sx={commonReturnTableStyle} align="center">
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap={1} // spacing between Approve & Reject
                        flexWrap="nowrap" // 🚀 prevent wrapping
                      >
                        {tour.status === Status.ACTIVE ? (
                          <Typography
                            color="success.main"
                            sx={{ fontWeight: "bold" }}
                          >
                            Approved
                          </Typography>
                        ) : tour.status === Status.IN_ACTIVE ? (
                          <Typography
                            color="error.main"
                            sx={{ fontWeight: "bold" }}
                          >
                            Rejected
                          </Typography>
                        ) : (
                          <>
                            <Button
                              variant="outlined"
                              size="small"
                              color="success"
                              sx={{
                                minWidth: { xs: 50, sm: 60 },
                                py: 0.3,
                                fontSize: { xs: "0.6rem", sm: "0.7rem" },
                              }}
                              disabled={
                                selectedRows.length === 0 ||
                                selectedRows.length > 1 ||
                                !isSelected(tour.id)
                              }
                              onClick={() => handleSingleApprove(tour.id)}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              sx={{
                                minWidth: { xs: 50, sm: 60 },
                                py: 0.3,
                                fontSize: { xs: "0.6rem", sm: "0.7rem" },
                              }}
                              disabled={
                                selectedRows.length === 0 ||
                                selectedRows.length > 1 ||
                                !isSelected(tour.id)
                              }
                              onClick={() => handleSingleReject(tour.id)}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      {pagination && tourDeviationApprovalData.length > 0 && (
        <Box display="flex" justifyContent="center" mt={2} mb={2}>
          <Pagination
            count={Math.ceil(pagination.total / pagination.per_page)}
            page={page}
            variant="outlined"
            color="primary"
            onChange={handlePageChange}
          />
        </Box>
      )}

      <TourApprovalConfirmationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirmAction}
        selectedCount={
          selectedTourId === null ? selectedRows.length : undefined
        }
        title={
          selectedTourId === null
            ? "Bulk Action on Selected Deviation"
            : forcedAction === "approve"
            ? "Approve Tour Deviation"
            : "Reject Tour Deviation"
        }
        message={
          selectedTourId !== null
            ? "Are you sure you want to perform this action?"
            : undefined
        }
        forcedAction={forcedAction}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default TourDeviationApproval;
